import { useEffect, useState } from "react";
import { Box, Button, CircularProgress, MenuItem, Paper, Select, Table, TableBody, TableContainer, TableHead, TableRow, TextField, Typography, useTheme } from "@mui/material";
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import CircleIcon from '@mui/icons-material/Circle';
import Fetch from "../../../services/Fetch";
import { useConstants } from "../../../hooks/UseConstants";
import { useTableStyles } from "../../../hooks/UseTableStyles";
import { usePopups } from "../../../hooks/UsePopups";
import { useWaits } from "../../../hooks/UseWait";
import DeleteDialog from "../../../popup/DeleteDialog";
import { FormattedMessage, useIntl } from "react-intl";
import { usePagination } from "../../../hooks/UsePagination";
import { useSearch } from "../../../hooks/UseSearch";
import ModalOverlay from "../../../components/ModalOverlay";

function buildFormData(fields) {
    const fd = new FormData();
    Object.entries(fields).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') fd.append(k, v);
    });
    return fd;
}

function SimpleEntityTab({ config, setSnackBar }) {
    const intl = useIntl();
    const theme = useTheme();
    const { setPopup } = usePopups();
    const { host, language } = useConstants();
    const { StyledTableCell, StyledTableRow } = useTableStyles();
    const { search, setSearch } = useSearch();
    const { getWait, setGetWait, sendWait, setSendWait } = useWaits();
    const { page, setPage, currentPage, setCurrentPage, totalPages, setTotalPages } = usePagination();
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [item, setItem] = useState(null);
    const [deleteId, setDeleteId] = useState('');
    const [nameEn, setNameEn] = useState('');
    const [nameAr, setNameAr] = useState('');
    const [isActive, setIsActive] = useState('1');
    const popupPrefix = config.endpoint.replace(/-/g, '_');

    const fetchList = async () => {
        const result = await Fetch(
            host + `/${config.endpoint}?page=${page + 1}&search=${encodeURIComponent(search)}`,
            'GET',
            null
        );
        if (result.status === 200) {
            setItems(result.data.data || []);
            setTotal(result.data.pagination?.total ?? (result.data.data?.length || 0));
            setTotalPages(result.data.pagination?.last_page || 1);
            setCurrentPage(page);
        }
        setGetWait(false);
    };

    const resetForm = () => {
        setNameEn('');
        setNameAr('');
        setIsActive('1');
        setItem(null);
    };

    const openAdd = () => {
        resetForm();
        setPopup(`${popupPrefix}_add`, 'flex');
    };

    const openUpdate = (row) => {
        setItem(row);
        setNameEn(row.name_en || '');
        setNameAr(row.name_ar || '');
        setIsActive(row.is_active ? '1' : '0');
        setPopup(`${popupPrefix}_update`, 'flex');
    };

    const save = async (isUpdate) => {
        setSendWait(true);
        const fields = { name_en: nameEn, name_ar: nameAr };
        if (config.hasActive) fields.is_active = isActive;
        const formData = buildFormData(fields);
        const url = isUpdate
            ? `${host}/admin/${config.endpoint}/${item.id}/update`
            : `${host}/admin/${config.endpoint}/store`;
        const result = await Fetch(url, 'POST', formData);
        if (result.status === 200) {
            setSnackBar('success', <FormattedMessage id={isUpdate ? config.updatedMsgId : config.addedMsgId} />);
            await fetchList();
            resetForm();
            setPopup(isUpdate ? `${popupPrefix}_update` : `${popupPrefix}_add`, 'none');
        } else if (result.status === 422) {
            setSnackBar('error', result.data.errors?.[0] || result.data.message);
        }
        setSendWait(false);
    };

    const remove = async () => {
        const result = await Fetch(host + `/admin/${config.endpoint}/${deleteId}/delete`, 'DELETE', null);
        if (result.status === 200) {
            setItems((list) => list.filter((i) => i.id !== deleteId));
            setSnackBar('success', <FormattedMessage id={config.deletedMsgId} />);
            setPopup(`${popupPrefix}_delete`, 'none');
        }
    };

    useEffect(() => {
        setGetWait(true);
        fetchList();
    }, [page, search]);

    const FormPopup = ({ id, titleId, onSave }) => (
        <ModalOverlay id={id} onClose={() => { resetForm(); setPopup(id, 'none'); }}>
            <Box sx={{ backgroundColor: theme.palette.background.paper }} className="shadow-lg w-2/5 min-w-[320px] rounded-3xl px-4 py-5 relative" dir={language === 'en' ? 'ltr' : 'rtl'}>
                <Typography variant="h6" className="!font-semibold"><FormattedMessage id={titleId} /></Typography>
                <CloseIcon onClick={() => { resetForm(); setPopup(id, 'none'); }} className="cursor-pointer absolute top-5 left-5" sx={{ left: language === 'en' && '90%' }} />
                <TextField variant="outlined" label={<FormattedMessage id="category_name_ar" />} className="w-full !mt-5" value={nameAr} onChange={(e) => setNameAr(e.target.value)} />
                <TextField variant="outlined" label={<FormattedMessage id="category_name_en" />} className="w-full !mt-3" value={nameEn} onChange={(e) => setNameEn(e.target.value)} />
                {config.hasActive && (
                    <Box className="mt-4">
                        <Typography variant="body2" className="text-gray-500 mb-1"><FormattedMessage id="ads_state" /></Typography>
                        <Select fullWidth value={isActive} onChange={(e) => setIsActive(e.target.value)}>
                            <MenuItem value="1"><CircleIcon className="text-green-700" fontSize="small" /> <FormattedMessage id="active" /></MenuItem>
                            <MenuItem value="0"><CircleIcon className="text-gray-700" fontSize="small" /> <FormattedMessage id="stopped" /></MenuItem>
                        </Select>
                    </Box>
                )}
                <Box className="flex gap-2 mt-5">
                    <Button variant="contained" onClick={onSave} disabled={sendWait}>
                        {sendWait ? <CircularProgress size={20} color="inherit" /> : <FormattedMessage id="save" />}
                    </Button>
                    <Button variant="contained" className="!bg-gray-400" onClick={() => setPopup(id, 'none')}><FormattedMessage id="cancel" /></Button>
                </Box>
            </Box>
        </ModalOverlay>
    );

    return (
        <>
            {getWait ? (
                <Box className="flex justify-center py-20"><CircularProgress size={50} /></Box>
            ) : (
                <Box sx={{ backgroundColor: theme.palette.background.paper }} className="rounded-xl px-2">
                    <Box className="flex justify-between items-center px-2 py-2">
                        <Typography variant="h6"><FormattedMessage id={config.titleId} /></Typography>
                        <Button variant="contained" onClick={openAdd}><AddIcon /><FormattedMessage id={config.addTitleId} /></Button>
                    </Box>
                    <TableContainer component={Paper} dir={language === 'en' ? 'ltr' : 'rtl'}>
                        <Box className="py-2 px-2 flex justify-between items-center">
                            <Box className="w-2/4 relative">
                                <input
                                    style={{ backgroundColor: theme.palette.background.default }}
                                    onChange={(e) => { setPage(0); setSearch(e.target.value); }}
                                    className="w-full h-12 rounded-md border indent-14 outline-none"
                                    placeholder={intl.formatMessage({ id: config.searchId })}
                                />
                                <SearchOutlinedIcon className="absolute top-1/2 -translate-y-1/2 right-3 text-gray-500" />
                            </Box>
                            <Typography variant="body2" className="text-gray-500">
                                <FormattedMessage id={config.totalId} />: {total}
                            </Typography>
                        </Box>
                        <Table sx={{ minWidth: 500 }}>
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell align={language === 'en' ? 'left' : 'right'}><FormattedMessage id="category_name" /></StyledTableCell>
                                    {config.hasActive && <StyledTableCell align="center"><FormattedMessage id="ads_state" /></StyledTableCell>}
                                    <StyledTableCell align="center"><FormattedMessage id="procedures" /></StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.map((row) => (
                                    <StyledTableRow key={row.id}>
                                        <StyledTableCell align={language === 'en' ? 'left' : 'right'}>{language === 'en' ? row.name_en : row.name_ar}</StyledTableCell>
                                        {config.hasActive && (
                                            <StyledTableCell align="center">
                                                {row.is_active ? <FormattedMessage id="active" /> : <FormattedMessage id="stopped" />}
                                            </StyledTableCell>
                                        )}
                                        <StyledTableCell align="center" className="!flex justify-center gap-2">
                                            <Button size="small" variant="contained" className="!bg-green-300 !text-green-800" onClick={() => openUpdate(row)}><FormattedMessage id="update" /></Button>
                                            <Button size="small" variant="contained" className="!bg-red-300 !text-red-700" onClick={() => { setDeleteId(row.id); setPopup(`${popupPrefix}_delete`, 'flex'); }}><FormattedMessage id="delete" /></Button>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <Box className="flex justify-center items-center py-3" dir="rtl">
                            <Button disabled={page + 1 >= totalPages} onClick={() => setPage(currentPage + 1)}><NavigateNextIcon /></Button>
                            <Typography dir="ltr">{currentPage + 1} / {totalPages}</Typography>
                            <Button disabled={page === 0} onClick={() => setPage(currentPage - 1)}><NavigateBeforeIcon /></Button>
                        </Box>
                    </TableContainer>
                </Box>
            )}
            <FormPopup id={`${popupPrefix}_add`} titleId={config.addTitleId} onSave={() => save(false)} />
            <FormPopup id={`${popupPrefix}_update`} titleId={config.updateTitleId} onSave={() => save(true)} />
            <ModalOverlay id={`${popupPrefix}_delete`} onClose={() => setPopup(`${popupPrefix}_delete`, 'none')}>
                <DeleteDialog
                    onClickConfirm={remove}
                    onClickCancel={() => setPopup(`${popupPrefix}_delete`, 'none')}
                    title={<FormattedMessage id={config.deleteTitleId} />}
                    subtitle={<FormattedMessage id={config.deleteDescId} />}
                />
            </ModalOverlay>
        </>
    );
}

export default SimpleEntityTab;
